//@flow
'use client';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import 'video.js/dist/video-js.css';
import 'videojs-ima/dist/videojs.ima.css';
import './videojs.css';
import { mediaQuery } from '@/common/styleguide';
import Script from 'next/script';
import { preload } from 'react-dom';
import isServer from '@/util/isServer';

if (typeof window !== 'undefined') {
  window.VIDEOJS_NO_DYNAMIC_STYLE = true;
}

export const VideoJSPlayer = (props) => {
  const videoRef = useRef(null);
  const playerRef = useRef<Player>(null);
  const { src, onReady, poster } = props;
  const [imaLoaded, setImaLoaded] = useState(
    !isServer() && globalThis.google && globalThis.google.ima,
  );
  const [imaLoadFailed, setImaLoadFailed] = useState(false);
  const [fixed, setFixed] = useState(false);
  const visibiltyRef = useRef(undefined);

  preload(poster, { fetchPriority: 'high', as: 'image' });

  const options = useMemo(
    () => ({
      muted: true,
      playsinline: true,
      autoplay: 'muted',
      controls: true,
      responsive: true,
      fluid: true,
      aspectRatio: '16:9',
      poster: props.poster,
      sources: [
        {
          src,
          type: 'video/mp4',
        },
      ],
    }),
    [props.poster, src],
  );

  const getImaOptions = useCallback(async () => {
    return new Promise((res, rej) => {
      const adTagUrlDev =
        'https://pubads.g.doubleclick.net/gampad/ads?' +
        'sz=640x480&iu=/124319096/external/single_ad_samples&ciu_szs=300x250&' +
        'impl=s&gdfp_req=1&env=vp&output=vast&unviewed_position_start=1&' +
        'cust_params=deployment%3Ddevsite%26sample_ct%3Dlinear&correlator=';

      const isMobile = window.innerWidth < mediaQuery.minWidthDesktop;
      const ad = isMobile ? props.mobileAd : props.desktopAd;

      let adTagUrlProd = new URL(
        isMobile
          ? 'https://fast.nexx360.io/vast?tag_id=ghpqeumu&vf_id=1179&gdpr=1'
          : 'https://fast.nexx360.io/vast?tag_id=g5y095wv&vf_id=1178&gdpr=1',
      );

      // __tcfapi is not aviable on dev localhost
      if (window.ENV === 'development') {
        var imaOptions = {
          id: 'vid1',
          adTagUrl: adTagUrlDev,
        };

        return res(imaOptions);
      }

      window.__tcfapi('addEventListener', 2, (tcData) => {
        //CMP is not ready
        if (tcData == null || typeof tcData.eventStatus === 'undefined') return;

        //User interaction is not done yet
        if (
          tcData.eventStatus !== 'useractioncomplete' &&
          tcData.eventStatus !== 'tcloaded'
        )
          return;

        adTagUrlProd.searchParams.append('url', props.canonical_url);
        adTagUrlProd.searchParams.append('gdpr_consent', tcData.tcString);

        const adTagUrl =
          window.ENV === 'development' ? adTagUrlDev : adTagUrlProd.toString();

        var imaOptions = {
          id: 'vid1',
          adTagUrl: adTagUrl,
        };

        res(imaOptions);

        window.__tcfapi('removeEventListener', 2, () => {}, tcData.listenerId);
      });
    });
  }, [props.canonical_url, props.desktopAd, props.mobileAd]);

  useEffect(() => {
    if (!playerRef.current && imaLoaded) {
      (async () => {
        const videojs = (await import('video.js')).default;
        const ads = (await import('videojs-contrib-ads')).default;
        videojs.registerPlugin('ads', ads);
        await import('videojs-ima');

        const imaOptions = await getImaOptions();

        if (!videoRef.current) return;

        const player = (playerRef.current = videojs(
          videoRef.current,
          options,
          () => {
            player.play();
            videojs.log('player is ready');
            onReady && onReady(player);
          },
        ));
        player.ima(imaOptions);
      })();
    }
  }, [
    getImaOptions,
    imaLoaded,
    onReady,
    options,
    props.canonical_url,
    props.desktopAd,
    props.mobileAd,
    videoRef,
  ]);

  useLayoutEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!fixed && !entry.isIntersecting) {
          setFixed(true);
        } else if (fixed && entry.isIntersecting) {
          setFixed(false);
        }
      },
      {
        root: null, // viewport
        rootMargin: '0px', // no margin
        threshold: 0.1, // 50% of target visible
      },
    );

    if (visibiltyRef.current) {
      observer.observe(visibiltyRef.current);
    }

    // Clean up the observer
    return () => {
      if (visibiltyRef.current) {
        observer.unobserve(visibiltyRef.current);
      }
    };
  }, [fixed]);

  return (
    <div ref={visibiltyRef}>
      <figure
        className={
          fixed
            ? 'fixed top-[120px] md:top-auto md:bottom-4 left-4 md:left-1/2  w-60 md:w-80 z-[2000] overflow-hidden rounded-lg transition-all'
            : 'relative w-full overflow-hidden transition-all'
        }
      >
        <video
          ref={videoRef}
          id="vid1"
          className="video-js vjs-16-9 vjs-default-skin"
          preload="auto"
          poster={poster}
          muted
          width="640"
          height="360"
          playsInline
          controls
        >
          <source src={options.sources[0].src} />
        </video>
        <Script
          src="https://imasdk.googleapis.com/js/sdkloader/ima3.js"
          onReady={() => {
            setImaLoaded(true);
          }}
          onError={() => {
            setImaLoadFailed(true);
          }}
        ></Script>
      </figure>
    </div>
  );
};

export default VideoJSPlayer;
